import * as React from "react";
import { Box, CPText } from "../../../shared/components/base";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSubtitle,
  CardTitle,
  StackLayout,
} from "@progress/kendo-react-layout";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import type { CPTextBoxChangeEvent } from "shared/components/shared/CPTextBox/CPTextBox";
import CPDropDownList from "shared/components/shared/CPDropDownList/CPDropDownList";
import type { CPDropDownListEventChangeEvent } from "shared/components/shared/CPDropDownList/CPDropDownList";
import { useCreatePendingUserMutation } from "data/pendingUser/pendingUser";
import logo from "../../../assets/evans-bi-logo2.png";
import "./RequestAccessPageStyles.scss";
import { useNavigate, useLocation } from "react-router-dom";

const REQUEST_TYPES = [
  "Agency Manager / Owner",
  "Agent Employee",
  "Owner Operator / Fleet Owner",
  "Driver",
  
];

interface FormState {
  firstname: string;
  lastname: string;
  email: string;
  requestType: string;
  agencyCode: string;
  externalSystemId: string;
}

const initialForm: FormState = {
  firstname: "",
  lastname: "",
  email: "",
  requestType: "",
  agencyCode: "",
  externalSystemId: "",
};

interface FormErrors {
  firstname?: string;
  lastname?: string;
  email?: string;
  requestType?: string;
  agencyCode?: string;
  externalSystemId?: string;
}

const agencyRequiresSelection = (requestType: string) =>
  requestType === "Agency Manager / Owner" ||
  requestType === "Agent Employee" ||
  requestType === "Driver" ||
  requestType === "Owner Operator / Fleet Owner";

const DRIVER_CODE_REQUEST_TYPES = ["Driver", "Owner Operator / Fleet Owner"];

const externalSystemIdRequired = (requestType: string) =>
  DRIVER_CODE_REQUEST_TYPES.includes(requestType);

const RATE_LIMIT_KEY = "request_access_submissions";
const RATE_LIMIT_WINDOW_MS = 5 * 60 * 1000;
const MAX_SUBMISSIONS_PER_WINDOW = 3;

interface RateLimitData {
  timestamps: number[];
}

const checkAndUpdateRateLimit = (): {
  allowed: boolean;
  retryAfterSeconds?: number;
} => {
  const now = Date.now();
  const storedData = localStorage.getItem(RATE_LIMIT_KEY);
  let data: RateLimitData = { timestamps: [] };

  if (storedData) {
    try {
      data = JSON.parse(storedData);
    } catch (e) {
      data = { timestamps: [] };
    }
  }

  const validTimestamps = data.timestamps.filter(
    (ts) => now - ts < RATE_LIMIT_WINDOW_MS,
  );

  if (validTimestamps.length >= MAX_SUBMISSIONS_PER_WINDOW) {
    const oldestTimestamp = Math.min(...validTimestamps);
    const retryAfterMs = RATE_LIMIT_WINDOW_MS - (now - oldestTimestamp);
    const retryAfterSeconds = Math.ceil(retryAfterMs / 1000);
    return { allowed: false, retryAfterSeconds };
  }

  validTimestamps.push(now);
  localStorage.setItem(
    RATE_LIMIT_KEY,
    JSON.stringify({ timestamps: validTimestamps }),
  );
  return { allowed: true };
};

const RequestAccessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as { email?: string; reason?: string }) ?? {};
  const prefillEmail = locationState.email ?? "";
  const [form, setForm] = React.useState<FormState>({
    ...initialForm,
    email: prefillEmail,
  });
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [submitted, setSubmitted] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [infoMsg] = React.useState<string | null>(
    locationState.reason === "no_account"
      ? "No account was found for that email address. Please complete the form below to request access."
      : null,
  );

  const {
    mutate: createRequest,
    isLoading: submitting,
    error: createRequestError,
  } = useCreatePendingUserMutation();

  const handleTextChange = (e: CPTextBoxChangeEvent) => {
    setForm((prev) => ({ ...prev, [e.name]: e.value as string }));
    setErrors((prev) => ({ ...prev, [e.name]: undefined }));
  };

  const handleRequestTypeChange = (e: CPDropDownListEventChangeEvent) => {
    setForm((prev) => ({
      ...prev,
      requestType: e.value as string,
      agencyCode: "",
    }));
    setErrors((prev) => ({
      ...prev,
      requestType: undefined,
      agencyCode: undefined,
    }));
  };

  const handleSubmit = () => {
    const rateLimit = checkAndUpdateRateLimit();
    if (!rateLimit.allowed) {
      const minutes = Math.ceil((rateLimit.retryAfterSeconds ?? 1) / 60);
      setErrorMsg(
        `Too many requests. Please try again in ${minutes} minute${minutes > 1 ? "s" : ""}.`,
      );
      return;
    }

    if (
      externalSystemIdRequired(form.requestType) &&
      !form.externalSystemId.trim()
    ) {
      setErrors((prev) => ({
        ...prev,
        externalSystemId: "Driver Code/Username is required.",
      }));
      return;
    }

    createRequest(
      {
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        email: form.email.trim(),
        requestType: form.requestType,
        agencies: agencyRequiresSelection(form.requestType)
          ? form.agencyCode
              .split(/[;,]/)
              .map((code) => code.trim())
              .filter(Boolean)
          : undefined,
        externalSystemId: form.externalSystemId.trim() || undefined,
      },
      {
        onSuccess: () => {
          setErrorMsg(null);
          setSubmitted(true);
        },
        onError: (error: unknown) => {
          if ((error as Record<string, unknown>)["errors"]) {
            setErrorMsg("Please check the form for errors and try again");
            return;
          }

          setErrorMsg(
            "An error occurred during the request. Please try again later.",
          );
        },
      },
    );
  };

  if (submitted) {
    return (
      <Card className="request-access-card">
        <CardHeader>
          <Box
            alignItems="center"
            justifyContent="center"
            className="logo-container"
          >
            <img
              src={logo}
              alt="BI Hub Logo"
              className="login-logo"
              width="250"
            />
          </Box>
          <CardTitle />
        </CardHeader>
        <CardBody>
          <StackLayout
            orientation="vertical"
            gap={16}
            align={{ horizontal: "center" }}
          >
            <CPText textStyle="h5" align="center">
              Request Submitted
            </CPText>
            <CPText textStyle="subtitle" align="center">
              Your request has been received. An administrator will review it
              and follow up with you at <strong>{form.email}</strong>.
            </CPText>
          </StackLayout>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="request-access-card">
      <CardHeader>
        <Box
          alignItems="center"
          justifyContent="center"
          className="logo-container"
        >
          <img
            src={logo}
            alt="BI Hub Logo"
            className="login-logo"
            width="250"
          />
        </Box>
        <CardTitle />
        <CardSubtitle>
          <CPText textStyle="subtitle" align="center">
            Request access to the Evans Business Intelligence Hub
          </CPText>
        </CardSubtitle>
      </CardHeader>

      <CardBody>
        <StackLayout orientation="vertical" gap={8}>
          <CPText textStyle="h6" align="center">
            Need to request access?
          </CPText>
          <CPText align="center" className="request-access-help-text">
            Use the form below if you do not have an Evans account. This request
            is for users outside the Evans Microsoft account system.
          </CPText>
        </StackLayout>

        {errorMsg && <CPText color="error">{errorMsg}</CPText>}
        {infoMsg && (
          <div className="request-access-info-banner">
            <CPText color="info" fontSize="18px" align="center">
              {infoMsg}
            </CPText>
          </div>
        )}
        <StackLayout orientation="vertical" gap={16}>
          <StackLayout orientation="horizontal" gap={12}>
            <div style={{ flex: 1 }}>
              <CPTextBox
                name="firstname"
                label="First Name"
                error={createRequestError}
                required
                personNameCharacters
                toCapital
                value={form.firstname}
                errorMsg={errors.firstname}
                onChange={handleTextChange}
              />
            </div>
            <div style={{ flex: 1 }}>
              <CPTextBox
                name="lastname"
                label="Last Name"
                error={createRequestError}
                required
                personNameCharacters
                toCapital
                value={form.lastname}
                errorMsg={errors.lastname}
                onChange={handleTextChange}
              />
            </div>
          </StackLayout>

          <CPTextBox
            name="email"
            label="Email Address"
            required
            value={form.email}
            errorMsg={errors.email}
            onChange={handleTextChange}
            error={createRequestError}
          />

          <CPDropDownList
            name="requestType"
            label="Request Type"
            required
            data={REQUEST_TYPES}
            value={form.requestType || null}
            errorMsg={errors.requestType}
            onChange={handleRequestTypeChange}
          />

          <CPTextBox
            name="externalSystemId"
            label={
              DRIVER_CODE_REQUEST_TYPES.includes(form.requestType)
                ? "Driver Code/Username"
                : "Username"
            }
            required={externalSystemIdRequired(form.requestType)}
            value={form.externalSystemId}
            errorMsg={errors.externalSystemId}
            onChange={handleTextChange}
          />
          <CPText
            textStyle="caption"
            className="request-access-help-text"
            fontSize="14px"
            style={{ marginTop: -8 }}
          >
            Use the username that you normally use to access Settlements,
            GreatEdge, and/or Evans Portals
          </CPText>

          {agencyRequiresSelection(form.requestType) && (
            <CPTextBox
              name="agencyCode"
              label="Enter your primary agency code(s)"
              required
              value={form.agencyCode}
              errorMsg={errors.agencyCode}
              onChange={handleTextChange}
              error={createRequestError}
              helpText="Separate multiple codes with a comma or semicolon (e.g. 0285, ACROSS, EFLUTS)"
            />
          )}
        </StackLayout>
      </CardBody>

      <CardFooter>
        <StackLayout orientation="horizontal" gap={16}>
          <CPButton
            themeColor={"base"}
            disabled={submitting}
            onClick={() => navigate(-1)}
          >
            Go Back
          </CPButton>
          <CPButton
            themeColor="primary"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? "Submitting..." : "Submit Request"}
          </CPButton>
        </StackLayout>
      </CardFooter>
    </Card>
  );
};

export default RequestAccessPage;
